/**
 * F17 T17.1.6 v3 Commit 6 — zakładka "Moje zgłoszenia" (instance-level).
 *
 * Endpoint: `GET /api/processes/my-submitted-instances` (Commit 3).
 * Backend filtruje `submitted_by_user_id = me` — bez explicit permission gate.
 * Cross-company (instancje z każdej firmy w której user ma kontekst).
 *
 * Filtry per kolumna (Kreator Listy, popup w nagłówku `InstanceListingTable`) —
 * server-side przez `?headerFilters=` (mirror `ActiveInstancesTab.tsx`). Zmiana
 * filtra resetuje `offset` do 0. Whitelist backendu → `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/processes/my-submitted-instances/route.ts`.
 *
 * Grupowanie (fala 2 kokpitu decyzji, `?groupBy=`, biała lista `SUBMITTED_INSTANCE_GROUP_BY`
 * z `@saas/shared` — proces/kartoteka/status) — LOKALNY stan (`groupByValue`), NIE URL-owy jak
 * na „Moje czekające" (ten ekran nie ma deep-linkowanych kafelków sterujących wymiarem).
 * `groupedQuery` dociąga WYŁĄCZNIE nagłówki koszyków (liczność; `sumsByCurrency` zawsze puste —
 * ten ekran nie ma kolumny „Wartość"). Koszyki startują ZWINIĘTE; klik nagłówka (sterowanie
 * `InstanceListingTable`/`ConfigurableTable` przez `expandedGroupKeys`/`onToggleGroup`) dociąga
 * wiersze TEGO koszyka leniwie — osobne żądanie `fetchSubmittedInstanceGroupRows` per rozwinięty
 * klucz, z WŁASNĄ mini-paginacją. Mechanika (stan rozwinięcia/paginacji, `useQueries`,
 * mapowanie na kształt `ConfigurableTable.groups`) żyje we WSPÓLNYM haku `useLazyGroupRows`
 * (`./use-lazy-group-rows.tsx`) — mirror `MyPendingDecisionsTab.tsx`/`ActiveInstancesTab.tsx`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DecisionGroupBy, SUBMITTED_INSTANCE_GROUP_BY } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import type { ColumnHeaderFilters } from '@/components/configurable-table';
import { Dropdown } from '@/components/Dropdown';
import {
  type DecisionGroupHeader,
  fetchGroupedSubmittedInstances,
  fetchMySubmittedInstances,
  fetchSubmittedInstanceGroupRows,
  type InstanceListingRow,
} from '@/lib/processes/approvals-api';

import {
  buildInstanceListingColumns,
  InstanceListingTable,
  serializeHeaderFiltersForRequest,
} from './InstanceListingTable';
import { PaginationControls } from './PaginationControls';
import { useLazyGroupRows } from './use-lazy-group-rows';

const PAGE_LIMIT = 50;

export function MySubmittedInstancesTab() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Grupowanie (fala 2) — `''` = bez grupowania (dotychczasowe zachowanie).
  const [groupByValue, setGroupByValue] = useState<DecisionGroupBy | ''>('');
  const groupBy: DecisionGroupBy | null = groupByValue || null;
  const isGrouped = groupBy !== null;

  const columns = useMemo(() => buildInstanceListingColumns(false, t), [t]);
  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );
  const sortParam = sortColumn ? `${sortColumn}:${sortDirection}` : undefined;

  function handleHeaderFiltersChange(next: ColumnHeaderFilters) {
    setHeaderFilters(next);
    setOffset(0);
  }

  // Sort server-side (kontrolowany) — reset offset (zbiór stron się zmienia).
  function handleSort(column: string, direction: 'asc' | 'desc') {
    setSortColumn(column);
    setSortDirection(direction);
    setOffset(0);
  }

  // Tryb płaski (paginowany, dotychczasowy) — aktywny wyłącznie bez `groupBy`.
  const flatQuery = useQuery({
    queryKey: [
      'processes',
      'my-submitted-instances',
      {
        offset,
        limit: PAGE_LIMIT,
        headerFilters: serializedHeaderFilters,
        sort: sortParam ?? null,
      },
    ],
    queryFn: () =>
      fetchMySubmittedInstances({
        limit: PAGE_LIMIT,
        offset,
        headerFilters: serializedHeaderFilters,
        sort: sortParam,
      }),
    enabled: !isGrouped,
  });

  // Tryb grupowany — nagłówki koszyków, BEZ paginacji (agregacja SQL wszystkich pasujących wierszy).
  const groupedQuery = useQuery({
    queryKey: [
      'processes',
      'my-submitted-instances',
      'grouped',
      { groupBy, headerFilters: serializedHeaderFilters },
    ],
    queryFn: () => {
      // Strażnik TS — nieosiągalny w runtime (`enabled: isGrouped` gwarantuje `groupBy !== null`).
      if (groupBy === null) throw new Error('groupedQuery wywołane bez groupBy');
      return fetchGroupedSubmittedInstances({ groupBy, headerFilters: serializedHeaderFilters });
    },
    enabled: isGrouped,
  });

  const groups: DecisionGroupHeader[] = groupedQuery.data?.groups ?? [];

  // Wiersze rozwiniętych koszyków — LENIWE doładowanie (fala 2), wyciągnięte do WSPÓLNEGO
  // haka `useLazyGroupRows` (mirror `MyPendingDecisionsTab.tsx`/`ActiveInstancesTab.tsx`).
  // `queryKeyExtra` MUSI mirror'ować dokładnie to, co `fetchRows` czyta z domknięcia
  // (`serializedHeaderFilters`) — inaczej cache react-query się rozjedzie. Ten ekran nie ma
  // kolumny „Wartość" — `buildSummary` pominięte (`summary: null`).
  const { expandedGroupKeys, toggleGroupExpand, resetExpansion, mappedGroups } =
    useLazyGroupRows<InstanceListingRow>({
      groupBy,
      groups,
      enabled: isGrouped,
      queryKeyBase: ['processes', 'my-submitted-instances', 'group-rows'],
      queryKeyExtra: { headerFilters: serializedHeaderFilters, sort: sortParam ?? null },
      fetchRows: ({ groupKey, offset: rowsOffset, limit: rowsLimit }) => {
        if (groupBy === null) throw new Error('fetchRows (group-rows) wywołane bez groupBy');
        return fetchSubmittedInstanceGroupRows({
          groupBy,
          groupKey,
          limit: rowsLimit,
          offset: rowsOffset,
          headerFilters: serializedHeaderFilters,
          sort: sortParam,
        });
      },
      testIdPrefix: 'submitted',
    });

  function handleGroupByChange(value: string) {
    setGroupByValue(value === '' ? '' : (value as DecisionGroupBy));
    setOffset(0);
    // Zmiana wymiaru (lub wyjście z grupowania) czyści stan rozwinięcia/paginacji koszyków
    // (`useLazyGroupRows.resetExpansion`) — klucze są specyficzne dla wymiaru (id procesu vs
    // enum statusu…), mirror `MyPendingDecisionsTab.handleGroupByChange`.
    resetExpansion();
  }

  const groupByOptions = useMemo(
    () => [
      { value: '', label: t('tenant.processes.approvals.groupBy.none') },
      ...SUBMITTED_INSTANCE_GROUP_BY.map((dim) => ({
        value: dim,
        label: t(`tenant.processes.approvals.groupBy.${dim}`),
      })),
    ],
    [t],
  );

  const isLoading = isGrouped ? groupedQuery.isLoading : flatQuery.isLoading;
  const isError = isGrouped ? groupedQuery.isError : flatQuery.isError;

  if (isLoading) {
    return (
      <div className="text-sm text-text-tertiary" data-testid="approvals-loading">
        {t('tenant.processes.approvals.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-error" data-testid="approvals-load-error">
        {t('tenant.processes.approvals.loadError')}
      </div>
    );
  }

  const rows = flatQuery.data?.data ?? [];
  const total = flatQuery.data?.total ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <InstanceListingTable
          rows={isGrouped ? [] : rows}
          showSubmitter={false}
          testIdPrefix="submitted"
          emptyTitle={t('tenant.processes.approvals.empty.submitted')}
          headerFilters={headerFilters}
          onHeaderFiltersChange={handleHeaderFiltersChange}
          sortColumn={sortColumn ?? undefined}
          sortDirection={sortDirection}
          onSort={handleSort}
          groups={mappedGroups}
          expandedGroupKeys={isGrouped ? expandedGroupKeys : undefined}
          onToggleGroup={isGrouped ? toggleGroupExpand : undefined}
          toolbarExtra={
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap text-sm text-text-tertiary">
                {t('tenant.processes.approvals.groupBy.label')}
              </span>
              <Dropdown
                options={groupByOptions}
                value={groupByValue}
                onChange={handleGroupByChange}
                aria-label={t('tenant.processes.approvals.groupBy.label')}
                className="w-48"
                data-testid="submitted-groupby-select"
              />
            </div>
          }
        />
      </div>
      {/* Paginacja (trybu płaskiego) — ukryta w trybie grupowanym (nagłówki koszyków są
          agregacją SQL bez paginacji; wiersze KAŻDEGO koszyka mają WŁASNĄ mini-paginację
          w jego `footer`, patrz `mappedGroups`). */}
      {!isGrouped && (
        <div className="shrink-0">
          <PaginationControls
            total={total}
            offset={offset}
            limit={PAGE_LIMIT}
            onOffsetChange={setOffset}
          />
        </div>
      )}
    </div>
  );
}
